import { NumberField } from '@/components/Field';
import type { MetaKcal, TipoEtapa } from '@/db/types';
import { leerDecimal } from '@/domain/numbers';
import { validarMetas, type MetasEtapa } from '@/domain/etapas';
import { Opciones } from '@/features/food/comunes';

/**
 * Metas de una etapa en un formulario (Req. 21.2): kcal fija o contra el
 * mantenimiento, gramos de proteína, carbohidratos y grasa, y horas de ayuno.
 * Lo que se deja vacío queda sin meta, no en cero.
 */
export interface EstadoMetas {
  modo: 'fija' | 'mantenimiento' | 'porcentaje' | 'ninguna';
  signo: 'menos' | 'mas';
  /** kcal (fija o diferencia) o porcentaje, sin signo. */
  valor: string;
  proteina: string;
  carbohidratos: string;
  grasa: string;
  ayuno: string;
}

const texto = (v: number | null) => (v === null ? '' : String(v));

export function estadoDeMetas(m: MetasEtapa): EstadoMetas {
  const base = {
    proteina: texto(m.proteinaG),
    carbohidratos: texto(m.carbohidratosG),
    grasa: texto(m.grasaG),
    ayuno: texto(m.ayunoHoras),
  };
  const meta = m.metaKcal;
  if (meta === null) return { modo: 'ninguna', signo: 'menos', valor: '', ...base };
  if (meta.modo === 'fija') return { modo: 'fija', signo: 'menos', valor: texto(meta.kcal), ...base };
  const cifra = meta.modo === 'diferencia' ? meta.kcal : meta.pct;
  return {
    modo: meta.modo === 'diferencia' ? 'mantenimiento' : 'porcentaje',
    signo: cifra < 0 ? 'menos' : 'mas',
    valor: texto(Math.abs(cifra)),
    ...base,
  };
}

/** Las metas escritas, o el motivo por el que no se pueden usar. */
export function metasDeEstado(e: EstadoMetas, exigirKcal: boolean): MetasEtapa | string {
  const opcional = (t: string) => (t.trim() === '' ? null : leerDecimal(t));
  for (const t of [e.proteina, e.carbohidratos, e.grasa, e.ayuno]) {
    if (t.trim() !== '' && leerDecimal(t) === null) return 'Revisa las metas: deben ser números.';
  }
  let metaKcal: MetaKcal | null = null;
  if (e.modo !== 'ninguna') {
    const cifra = e.valor.trim() === '' && e.modo !== 'fija' ? 0 : leerDecimal(e.valor);
    if (cifra === null) return 'Escribe la meta de kcal.';
    const conSigno = e.signo === 'menos' ? -cifra : cifra;
    metaKcal =
      e.modo === 'fija'
        ? { modo: 'fija', kcal: cifra }
        : e.modo === 'mantenimiento'
          ? { modo: 'diferencia', kcal: conSigno === 0 ? 0 : conSigno }
          : { modo: 'porcentaje', pct: conSigno === 0 ? 0 : conSigno };
  } else if (exigirKcal) {
    return 'Define la meta de kcal de esta etapa.';
  }
  const metas: MetasEtapa = {
    metaKcal,
    proteinaG: opcional(e.proteina),
    carbohidratosG: opcional(e.carbohidratos),
    grasaG: opcional(e.grasa),
    ayunoHoras: opcional(e.ayuno),
  };
  return validarMetas(metas) ?? metas;
}

interface Props {
  estado: EstadoMetas;
  onCambio: (estado: EstadoMetas) => void;
  tipo: TipoEtapa;
}

export function CamposMetas({ estado, onCambio, tipo }: Props) {
  const cambiar = (cambios: Partial<EstadoMetas>) => onCambio({ ...estado, ...cambios });
  return (
    <div className="grid gap-[17px]">
      <Opciones
        etiqueta="Meta de kcal"
        valor={estado.modo}
        onCambio={(modo) => cambiar({ modo })}
        columnas={2}
        opciones={[
          { valor: 'fija', etiqueta: 'Fija' },
          { valor: 'mantenimiento', etiqueta: 'Contra mantenimiento' },
          { valor: 'porcentaje', etiqueta: '% del mantenimiento' },
          { valor: 'ninguna', etiqueta: 'Sin meta' },
        ]}
      />
      {estado.modo === 'fija' && (
        <NumberField etiqueta="kcal al día" valor={estado.valor} onCambio={(valor) => cambiar({ valor })} decimal={false} />
      )}
      {(estado.modo === 'mantenimiento' || estado.modo === 'porcentaje') && (
        <div className="grid grid-cols-2 items-end gap-3">
          <Opciones
            etiqueta="Respecto al mantenimiento"
            valor={estado.signo}
            onCambio={(signo) => cambiar({ signo })}
            columnas={2}
            opciones={[
              { valor: 'menos', etiqueta: 'Menos' },
              { valor: 'mas', etiqueta: 'Más' },
            ]}
          />
          <NumberField
            etiqueta={estado.modo === 'mantenimiento' ? 'kcal' : '%'}
            valor={estado.valor}
            onCambio={(valor) => cambiar({ valor })}
            decimal={false}
            placeholder="0"
          />
        </div>
      )}
      {estado.modo !== 'fija' && estado.modo !== 'ninguna' && (
        <p className="-mt-2 text-[12px] text-texto-suave">
          {tipo === 'mantenimiento'
            ? 'Con 0 la meta es tu mantenimiento tal cual. Si todavía no lo capturas, la app dice "falta el mantenimiento".'
            : 'Se calcula con el mantenimiento vigente de cada día y se redondea a 10 kcal.'}
        </p>
      )}
      <fieldset className="grid gap-2">
        <legend className="mb-1 text-[13px] text-texto-suave">Gramos al día (vacío = sin meta)</legend>
        <div className="grid grid-cols-3 gap-3">
          <NumberField etiqueta="Proteína" valor={estado.proteina} onCambio={(proteina) => cambiar({ proteina })} />
          <NumberField
            etiqueta="Carbohidratos"
            valor={estado.carbohidratos}
            onCambio={(carbohidratos) => cambiar({ carbohidratos })}
          />
          <NumberField etiqueta="Grasa" valor={estado.grasa} onCambio={(grasa) => cambiar({ grasa })} />
        </div>
      </fieldset>
      <NumberField etiqueta="Ayuno (horas)" valor={estado.ayuno} onCambio={(ayuno) => cambiar({ ayuno })} />
    </div>
  );
}
